import type { Pheromones, PheromoneChannel } from './contracts';

const LN2 = Math.LN2;

// 3×3 splat kernel, row-major, sums to 1. Center carries 0.4 so a deposit
// registers locally but a third leaks into neighbours to smooth sampling.
const KERNEL = [
  0.05, 0.1, 0.05,
  0.1,  0.4, 0.1,
  0.05, 0.1, 0.05,
] as const;

export class PheromoneGrid implements Pheromones {
  readonly size: number;
  readonly extent: number;
  readonly data: [Float32Array, Float32Array];

  private readonly cellSize: number;
  private readonly halfExtent: number;
  private readonly scratch: Float32Array;

  // Half-lives chosen so food trails fade faster than home trails: returning
  // ants need a persistent beacon back to the nest, while food scents should
  // rot once a pile is exhausted so the colony stops pulling on old routes.
  private readonly decayFood = LN2 / 12;
  private readonly decayHome = LN2 / 25;
  private readonly diffuseCoeff = 0.4;
  private decayMul = 1;

  constructor(size = 256, extent = 80) {
    this.size = size;
    this.extent = extent;
    this.cellSize = extent / size;
    this.halfExtent = extent / 2;
    const n = size * size;
    this.data = [new Float32Array(n), new Float32Array(n)];
    this.scratch = new Float32Array(n);
  }

  deposit(x: number, z: number, channel: PheromoneChannel, amount: number): void {
    if (amount === 0) return;
    const size = this.size;
    const fi = (x + this.halfExtent) / this.cellSize;
    const fj = (z + this.halfExtent) / this.cellSize;
    const ci = Math.floor(fi);
    const cj = Math.floor(fj);
    if (ci < -1 || cj < -1 || ci > size || cj > size) return;
    const grid = this.data[channel];
    for (let dy = -1; dy <= 1; dy++) {
      const jj = cj + dy;
      if (jj < 0 || jj >= size) continue;
      const row = jj * size;
      const krow = (dy + 1) * 3;
      for (let dx = -1; dx <= 1; dx++) {
        const ii = ci + dx;
        if (ii < 0 || ii >= size) continue;
        grid[row + ii] += amount * KERNEL[krow + (dx + 1)];
      }
    }
  }

  sample(x: number, z: number, channel: PheromoneChannel): number {
    const size = this.size;
    const fi = (x + this.halfExtent) / this.cellSize - 0.5;
    const fj = (z + this.halfExtent) / this.cellSize - 0.5;
    const i0 = Math.floor(fi);
    const j0 = Math.floor(fj);
    const i1 = i0 + 1;
    const j1 = j0 + 1;
    if (i1 < 0 || j1 < 0 || i0 >= size || j0 >= size) return 0;
    const tx = fi - i0;
    const tz = fj - j0;
    const grid = this.data[channel];
    // Bilinear (not nearest) so gradients along cell boundaries don't jump —
    // ants steering along the gradient would otherwise see discontinuous
    // targets at every crossing and visibly jitter.
    const c00 = (i0 >= 0 && j0 >= 0) ? grid[j0 * size + i0] : 0;
    const c10 = (i1 < size && j0 >= 0) ? grid[j0 * size + i1] : 0;
    const c01 = (i0 >= 0 && j1 < size) ? grid[j1 * size + i0] : 0;
    const c11 = (i1 < size && j1 < size) ? grid[j1 * size + i1] : 0;
    const a = c00 * (1 - tx) + c10 * tx;
    const b = c01 * (1 - tx) + c11 * tx;
    return a * (1 - tz) + b * tz;
  }

  gradient(x: number, z: number, channel: PheromoneChannel, out: [number, number]): void {
    const h = this.cellSize;
    const inv2h = 1 / (2 * h);
    const sxp = this.sample(x + h, z, channel);
    const sxn = this.sample(x - h, z, channel);
    const szp = this.sample(x, z + h, channel);
    const szn = this.sample(x, z - h, channel);
    out[0] = (sxp - sxn) * inv2h;
    out[1] = (szp - szn) * inv2h;
  }

  setDecayMultiplier(mul: number): void {
    this.decayMul = mul > 0 ? mul : 0;
  }

  update(dt: number): void {
    if (dt <= 0) return;
    const food = this.data[0];
    const home = this.data[1];
    const mul = this.decayMul;
    const decayFood = Math.exp(-this.decayFood * mul * dt);
    const decayHome = Math.exp(-this.decayHome * mul * dt);
    const n = food.length;
    for (let i = 0; i < n; i++) {
      food[i] *= decayFood;
      home[i] *= decayHome;
    }
    // Clamp blend weight into [0, 0.25] so the 5-point Laplacian stays stable
    // (max step for a 2D explicit diffuser on a 5-point stencil is 1/4).
    let k = this.diffuseCoeff * dt;
    if (k < 0) k = 0;
    if (k > 0.25) k = 0.25;
    if (k > 0) {
      this.diffuseChannel(food, k);
      this.diffuseChannel(home, k);
    }
  }

  private diffuseChannel(grid: Float32Array, k: number): void {
    const size = this.size;
    const tmp = this.scratch;
    for (let j = 0; j < size; j++) {
      const row = j * size;
      const up = j > 0 ? row - size : row;
      const dn = j < size - 1 ? row + size : row;
      for (let i = 0; i < size; i++) {
        const left = i > 0 ? grid[row + i - 1] : grid[row + i];
        const right = i < size - 1 ? grid[row + i + 1] : grid[row + i];
        const above = grid[up + i];
        const below = grid[dn + i];
        const c = grid[row + i];
        const lap = left + right + above + below - 4 * c;
        const v = c + k * lap;
        tmp[row + i] = v > 0 ? v : 0;
      }
    }
    grid.set(tmp);
  }
}
